import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Switch } from "../ui/switch";
import { 
  Plus, Trash2, Edit, Search, UserPlus, Shield, 
  Users, Briefcase, Award, ShoppingBag, RefreshCw
} from "lucide-react";
import { API } from "../../apiConfig";


const UsersManagementTab = ({ users, token, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [newUser, setNewUser] = useState({
    email: "",
    name: "",
    password: "changeme123",
    role: "buyer",
    business_name: "",
    commission_rate: 20,
    territory: "All Regions",
    require_password_change: true
  });

  const headers = { Authorization: `Bearer ${token}` };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === "all" || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.name) {
      toast.error("Email and name are required");
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/users/create-staff`, newUser, { headers });
      toast.success(`User ${newUser.email} created successfully!`);
      if (res.data.agent_code) {
        toast.info(`Agent Code: ${res.data.agent_code}`);
      }
      setShowAddDialog(false);
      setNewUser({
        email: "",
        name: "",
        password: "changeme123",
        role: "buyer",
        business_name: "",
        commission_rate: 20,
        territory: "All Regions",
        require_password_change: true
      });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedUser) return;
    
    setLoading(true);
    try {
      await axios.put(`${API}/admin/users/${selectedUser.user_id}/role`, {
        role: selectedUser.role,
        commission_rate: selectedUser.commission_rate || 20,
        territory: selectedUser.territory || "All Regions",
        require_password_change: false
      }, { headers });
      toast.success(`User role updated to ${selectedUser.role}`);
      setShowEditDialog(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API}/admin/users/${selectedUser.user_id}`, { headers });
      toast.success(`User ${selectedUser.email} deleted`);
      setShowDeleteDialog(false);
      setSelectedUser(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setLoading(false);
    }
  };

  const getRoleIcon = (role) => {
    switch(role) {
      case 'admin': return <Shield className="h-4 w-4" />;
      case 'marketing_manager': return <Briefcase className="h-4 w-4" />;
      case 'sales_agent': return <Briefcase className="h-4 w-4" />;
      case 'promoter': return <Award className="h-4 w-4" />;
      case 'wholesale': return <ShoppingBag className="h-4 w-4" />;
      default: return <Users className="h-4 w-4" />;
    }
  };

  const getRoleBadgeVariant = (role) => {
    switch(role) {
      case 'admin': return 'default';
      case 'marketing_manager': return 'default';
      case 'sales_agent': return 'secondary';
      case 'promoter': return 'outline';
      case 'wholesale': return 'outline';
      default: return 'outline';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Header with Search and Add */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex gap-3 flex-1 w-full sm:w-auto">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-xl"
              data-testid="user-search-input"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[180px] rounded-xl" data-testid="role-filter">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="marketing_manager">Marketing Manager</SelectItem>
              <SelectItem value="sales_agent">Sales Agent</SelectItem>
              <SelectItem value="promoter">Promoter</SelectItem>
              <SelectItem value="wholesale">Wholesale</SelectItem>
              <SelectItem value="buyer">Buyer</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button 
          onClick={() => setShowAddDialog(true)}
          className="rounded-full gap-2"
          data-testid="add-user-btn"
        >
          <UserPlus className="h-4 w-4" />
          Add User
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        {['admin', 'marketing_manager', 'sales_agent', 'promoter', 'wholesale', 'buyer'].map(role => (
          <Card key={role} className="rounded-xl">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2">
                {getRoleIcon(role)}
                <div>
                  <p className="text-2xl font-bold">{users.filter(u => u.role === role).length}</p>
                  <p className="text-xs text-muted-foreground capitalize">{role.replace('_', ' ')}s</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Users Table */}
      <Card className="rounded-2xl">
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Business</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.user_id} data-testid={`user-row-${user.user_id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{user.name}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getRoleBadgeVariant(user.role)} className="gap-1">
                        {getRoleIcon(user.role)}
                        {user.role?.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>{user.business_name || "-"}</TableCell>
                    <TableCell>
                      {user.role === "wholesale" && (
                        <Badge variant={user.wholesale_status === "approved" ? "default" : "secondary"}>
                          {user.wholesale_status || "pending"}
                        </Badge>
                      )}
                      {user.role === "sales_agent" && (
                        <Badge className="bg-green-500 text-white">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(user.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedUser({ ...user, commission_rate: 20, territory: "All Regions" });
                            setShowEditDialog(true);
                          }}
                          data-testid={`edit-user-${user.user_id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowDeleteDialog(true);
                          }}
                          disabled={user.role === 'admin'}
                          data-testid={`delete-user-${user.user_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add User Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Add New User
            </DialogTitle>
            <DialogDescription>
              Create a new user account. They will be prompted to change their password on first login.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="user@example.com"
                data-testid="new-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="Full Name"
                data-testid="new-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Temporary Password</Label>
              <Input
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Temporary password"
                data-testid="new-user-password"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select 
                value={newUser.role} 
                onValueChange={(v) => setNewUser({ ...newUser, role: v })}
              >
                <SelectTrigger data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="buyer">Buyer</SelectItem>
                  <SelectItem value="marketing_manager">Marketing Manager</SelectItem>
                  <SelectItem value="sales_agent">Sales Agent</SelectItem>
                  <SelectItem value="promoter">Promoter / Ambassador</SelectItem>
                  <SelectItem value="wholesale">Wholesale</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              {newUser.role === "marketing_manager" && (
                <p className="text-xs text-muted-foreground">
                  Full access to: Articles, Marketing, Ambassadors, Sales Dashboard. No access to: Payments, Site Settings, Delete actions.
                </p>
              )}
            </div>
            
            {newUser.role === "sales_agent" && (
              <>
                <div className="space-y-2">
                  <Label>Commission Rate (%)</Label>
                  <Input
                    type="number"
                    value={newUser.commission_rate}
                    onChange={(e) => setNewUser({ ...newUser, commission_rate: parseFloat(e.target.value) })}
                    data-testid="new-user-commission"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Territory</Label>
                  <Input
                    value={newUser.territory}
                    onChange={(e) => setNewUser({ ...newUser, territory: e.target.value })}
                    placeholder="e.g., NSW, Sydney Metro"
                    data-testid="new-user-territory"
                  />
                </div>
              </>
            )}
            
            <div className="space-y-2">
              <Label>Business Name (optional)</Label>
              <Input
                value={newUser.business_name}
                onChange={(e) => setNewUser({ ...newUser, business_name: e.target.value })}
                placeholder="Business name"
                data-testid="new-user-business"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-xl">
              <div>
                <Label>Require Password Change</Label>
                <p className="text-xs text-muted-foreground">User must change password on first login</p>
              </div>
              <Switch
                checked={newUser.require_password_change}
                onCheckedChange={(v) => setNewUser({ ...newUser, require_password_change: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddUser} disabled={loading} data-testid="confirm-add-user">
              {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Edit User Role
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-muted/30 rounded-xl">
                <p className="font-medium">{selectedUser.name}</p>
                <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select 
                  value={selectedUser.role} 
                  onValueChange={(v) => setSelectedUser({ ...selectedUser, role: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="buyer">Buyer</SelectItem>
                    <SelectItem value="marketing_manager">Marketing Manager</SelectItem>
                    <SelectItem value="sales_agent">Sales Agent</SelectItem>
                    <SelectItem value="promoter">Promoter / Ambassador</SelectItem>
                    <SelectItem value="wholesale">Wholesale</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {selectedUser.role === "sales_agent" && (
                <>
                  <div className="space-y-2">
                    <Label>Commission Rate (%)</Label>
                    <Input
                      type="number"
                      value={selectedUser.commission_rate}
                      onChange={(e) => setSelectedUser({ ...selectedUser, commission_rate: parseFloat(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Territory</Label>
                    <Input
                      value={selectedUser.territory}
                      onChange={(e) => setSelectedUser({ ...selectedUser, territory: e.target.value })}
                    />
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateRole} disabled={loading}>
              {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
              Update Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedUser?.name}</strong> ({selectedUser?.email})?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteUser}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-user"
            >
              {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsersManagementTab;
